import { APP } from '@advanza/advanza_generic'
import { ApiInstance } from '@advanza/api'
import { Input } from '@advanza/shadcn-ui/src/components/ui/input'
import { Icon, LoadingDots } from '@advanza/ui'
import { useChat, UseChatOptions } from '@ai-sdk/react'
import { DefaultChatTransport, ToolUIPart, UIMessage } from 'ai'
import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'

interface Props {
    apiUrl: string
    startChatOnMount?: boolean // Send an initial message to start the chat when it opens
    history?: object[] | null // Previous messages (fetch from backend)
    sessionId?: string // Previous session id (fetch from backend)
    closeChat?: () => void // Callback after chat is closed
}

/**
 * This LLM chat interface works with our backend Bedrock implementation: BedrockLLMConversationService.php
 *
 * */
export const BedrockChatInterface = ({
    apiUrl,
    history,
    sessionId,
    closeChat,
    startChatOnMount = false,
}: Props) => {
    const [input, setInput] = useState('')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const options: UseChatOptions<UIMessage> = {
        transport: new DefaultChatTransport({
            api: ApiInstance.getAddress() + apiUrl,
            credentials: 'include',
            headers: {
                'X-CSRF-Token': ApiInstance._getCsrfTokenFromCookie() ?? '',
            },
        }),
    }
    if (sessionId && history) {
        options.id = sessionId
        options.messages = transformBedrockMessagesToUIMessages(history)
    }
    const { messages, sendMessage, status } = useChat(options)

    const isLoading = status === 'streaming' || status === 'submitted'

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'end',
        })
    }, [messages])

    useEffect(() => {
        if (startChatOnMount && messages.length === 0) {
            sendMessage({ text: '', metadata: { is_init_msg: true } })
        }

        return () => {
            closeChat && closeChat()
        }
    }, [])

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!input.trim() || isLoading) return
        const userInput = input.trim()
        setInput('')
        await sendMessage({ text: userInput })
    }

    return (
        <div className="min-h-96 max-h-[80vh] flex flex-col">
            {/* AWS Nova Pro model uses pseudo-reasoning in it's own <thinking tags.*/}
            <style type="text/css" dangerouslySetInnerHTML={{ __html: ThinkingTagCss }} />

            <div className="flex-1 space-y-2 overflow-y-auto mb-4">
                {messages
                    // @ts-ignore
                    .filter((message) => !message?.metadata?.is_init_msg)
                    .map((message) => (
                        <div
                            key={message.id}
                            className={clsx(
                                'flex gap-3',
                                message.role === 'user' ? 'flex-row-reverse' : 'flex-row',
                            )}>
                            {/* Avatar (only for user messages) */}
                            {message.role === 'user' && (
                                <div className="flex-shrink-0">
                                    <img
                                        src={`${APP.S3_USERS()}${window._user.id}/${
                                            window._user.avatar_filename
                                        }`}
                                        alt="User"
                                        className="w-10 h-10 rounded-full object-cover"
                                    />
                                </div>
                            )}
                            {/* Message content */}
                            <div
                                className={clsx(
                                    `min-w-60 empty:hidden`,
                                    message.role === 'user' && 'bg-grey-200  rounded-xl',
                                    message.role !== 'user' && 'flex-1 w-full',
                                )}>
                                {message.parts.map((part, i) => {
                                    if (part.type.startsWith('tool-')) {
                                        const toolPart = part as ToolUIPart
                                        return (
                                            <details
                                                key={i}
                                                className="mt-2 bg-secondary-50 p-2 border-b-secondary-200 rounded my-2">
                                                <summary className="cursor-pointer text-md text-neutral-600 justify-between flex">
                                                    {toolPart.type.replace('tool-', '')}{' '}
                                                    <span className="text-sm opacity-50 text-neutral-500 truncate">
                                                        {' '}
                                                        {JSON.stringify(toolPart?.input, null, 2)}
                                                    </span>
                                                    <ToolIcon isLoading={!toolPart.output} />
                                                </summary>
                                                <pre className="mt-2 text-sm bg-neutral-100 p-2 rounded,max-w-full max-h-40 whitespace-break-spaces overflow-auto">
                                                    {JSON.stringify(toolPart.input || {}, null, 2)}
                                                    {JSON.stringify(toolPart.output || {}, null, 2)}
                                                </pre>
                                            </details>
                                        )
                                    }
                                    if (
                                        part.type === 'text' &&
                                        part.text !== '' &&
                                        part.text !== null
                                    ) {
                                        return (
                                            <div
                                                className="p-2 whitespace-pre-wrap"
                                                key={i}
                                                dangerouslySetInnerHTML={{ __html: part.text }}
                                            />
                                        )
                                    }

                                    return null
                                })}
                            </div>
                        </div>
                    ))}
                {isLoading && (
                    <div className="p-3 bg-neutral-100 rounded-lg">
                        <LoadingDots />
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <form onSubmit={handleSubmit} className="flex gap-2">
                <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    className="bg-grey-200 rounded h-8"
                    disabled={isLoading}
                />
                <button type="submit" disabled={!input || isLoading}>
                    <Icon name="send" />
                </button>
            </form>
        </div>
    )
}

interface ToolIconProps {
    isLoading: boolean
}

const ToolIcon = ({ isLoading }: ToolIconProps) => (
    <Icon
        fontSize={18}
        name={isLoading ? 'progress_activity' : 'check'}
        classNameIcon={isLoading ? 'secondary-900 animate-spin' : 'secondary-900'}
    />
)

// Bedrock message format based on the provided JSON example
interface BedrockMessage {
    role: 'user' | 'assistant' | 'system'
    content: Array<{
        text?: string
        toolResult?: {
            toolUseId: string
            content: Array<{
                json?: any
            }>
        }
    }>
    timestamp: number
    invocation_id: string
    user_id?: number
    username?: string
}

// Function to transform Bedrock messages from history in s3 to useChat UIMessage format
function transformBedrockMessagesToUIMessages(bedrockMessages: object[]): UIMessage[] {
    return bedrockMessages.map((msg: any) => {
        const bedrockMsg = msg as BedrockMessage
        const isTool = bedrockMsg?.content[0]?.toolResult
        return {
            id: bedrockMsg.invocation_id || `msg-${bedrockMsg.timestamp || Date.now()}`,
            role: isTool ? 'assistant' : bedrockMsg.role,
            metadata: {
                timestamp: bedrockMsg.timestamp,
                invocation_id: bedrockMsg.invocation_id,
                ...(bedrockMsg.user_id && { user_id: bedrockMsg.user_id }),
                ...(bedrockMsg.username && { username: bedrockMsg.username }),
            },
            parts:
                bedrockMsg.content?.map((contentItem) => {
                    // Handle tool results
                    if (contentItem.toolResult) {
                        const toolResult = contentItem.toolResult
                        const toolName = 'tool-result' // Generic tool name for results
                        const output = toolResult.content?.[0]?.json || toolResult.content || {}

                        return {
                            type: `tool-${toolName}`,
                            toolCallId: toolResult.toolUseId,
                            state: 'output-available',
                            input: {}, // Tool results don't have input in history
                            output: output,
                        }
                    }

                    // Handle regular text content
                    if (contentItem.text) {
                        return {
                            type: 'text',
                            text: contentItem.text,
                        }
                    }

                    // Fallback for other content types
                    return {
                        type: 'text',
                        text: '',
                    }
                }) || [],
        }
    })
}

/*
 * Nova Pro <thinking tag css.
 * */
const ThinkingTagCss = `
thinking {
  display: inline-block;
  padding:3px;
  max-height: 3rem;
  overflow: auto;
  box-shadow: 0 0 3px -1px rgba(0,0,0,0.7);
  border-radius: 6px;
  white-space: collapse;
  font-size: 11px;
  opacity: 0.8;
  }
  `
